import React, { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "src/components/ui/card";
import { useMutation } from "@tanstack/react-query";
import CenterPreloader from "src/components/loaders/CenterPreloader";
import { Button } from "src/components/ui/button";
import { useToastStore } from "src/store/toast";
import { Loader2, RefreshCcw, Settings, Activity, Clock, DollarSign, PieChart, Mail } from "lucide-react";
import MariaLocation from "src/components/popup/MariaLocation";
import WeeklyDealsEmailDialog from "src/components/admin/WeeklyDealsEmailDialog";
import { useMariaPackage } from "src/hooks/dashboard";
import { useMariaDataRequest } from "src/hooks/api/admin/maria";
import { analyzeMarket, findOpportunities } from "src/api/admin/maria";

const Dashboard: React.FC = () => {
  const { addToastDanger, addToastSuccess } = useToastStore();
  const [isMariaLocationOpen, setIsMariaLocationOpen] = React.useState(false);
  const [isWeeklyDealsEmailOpen, setIsWeeklyDealsEmailOpen] = React.useState(false);

  const handleMariaLocationOpenChange = (open: boolean) => {
    setIsMariaLocationOpen(open);
  };

  const handleWeeklyDealsEmailOpenChange = (open: boolean) => {
    setIsWeeklyDealsEmailOpen(open);
  };

  const { data: mariaRawData, isLoading, refetch } = useMariaDataRequest();

  const mariaData = useMemo(() => mariaRawData?.data, [mariaRawData]);

  const { data: mariaPackage, isLoading: isMariaPackageLoading } = useMariaPackage();

  const findOpportunitiesMutation = useMutation({
    mutationFn: async () => {
      const response = await findOpportunities();
      return response;
    },
    onSuccess: (data) => {
      addToastSuccess({
        title: "Success",
        body: "Successfully started finding opportunities",
      });
    },
    onError: (error) => {
      addToastDanger({
        title: "Error",
        body: "Failed to start finding opportunities",
      });
    },
  });

  const analyzeMarketMutation = useMutation({
    mutationFn: async () => {
      const response = await analyzeMarket();
      return response;
    },
    onSuccess: (data) => {
      addToastSuccess({
        title: "Success",
        body: "Market analysis started successfully",
      });
    },
    onError: (error) => {
      addToastDanger({
        title: "Error",
        body: "Failed to start market analysis",
      });
    },
  });

  if (isLoading) {
    return <CenterPreloader />;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Maria Dashboard</h1>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              refetch();
            }}
          >
            <RefreshCcw className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setIsMariaLocationOpen(true);
            }}
          >
            <Settings className="w-4 h-4" />
          </Button>
          <Button
            variant="default"
            onClick={() => findOpportunitiesMutation.mutate()}
            disabled={findOpportunitiesMutation.isLoading}
            className="bg-primary hover:bg-primary/60"
          >
            {findOpportunitiesMutation.isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Finding...
              </>
            ) : (
              "Find Opportunities"
            )}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setIsWeeklyDealsEmailOpen(true);
            }}
          >
            Send Weekly Deals Email
          </Button>
          <Button variant="secondary" onClick={() => analyzeMarketMutation.mutate()} disabled={analyzeMarketMutation.isLoading}>
            {analyzeMarketMutation.isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : (
              "Analyze Market"
            )}
          </Button>
        </div>
      </div>

      <MariaLocation open={isMariaLocationOpen} onOpenChange={handleMariaLocationOpenChange} packageId={Number(mariaPackage?.id) || 0} />
      <WeeklyDealsEmailDialog open={isWeeklyDealsEmailOpen} onOpenChange={handleWeeklyDealsEmailOpenChange} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Overview Section */}
        <Card className="col-span-full">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Activity className="w-5 h-5" />
              System Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground mb-0">Total Maria Instances</p>
                <p className="text-2xl font-bold mb-0">{mariaData?.overview.total_maria_instances}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground mb-0">Active Instances</p>
                <p className="text-2xl font-bold text-green-500 mb-0">{mariaData?.overview.active_maria_instances}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground mb-0">Total Markets</p>
                <p className="text-2xl font-bold mb-0">{mariaData?.overview.total_markets}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground mb-0">Avg Markets per Maria</p>
                <p className="text-2xl font-bold mb-0">{mariaData?.overview.avg_markets_per_maria}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground mb-0">Total Properties</p>
                <p className="text-2xl font-bold mb-0">{mariaData?.overview.total_properties}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground mb-0">Avg Properties per Maria</p>
                <p className="text-2xl font-bold mb-0">{mariaData?.overview.avg_properties_per_maria}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground mb-0">Total Parser Tasks Queued</p>
                <p className="text-2xl font-bold mb-0">{mariaData?.overview.total_parser_tasks_queued}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground mb-0">Total Parser Tasks Ran</p>
                <p className="text-2xl font-bold mb-0">{mariaData?.overview.total_parser_tasks_ran}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground mb-0">Total Parser Tasks Failed</p>
                <p className="text-2xl font-bold mb-0">{mariaData?.overview.total_parser_tasks_failed}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity Card */}
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground mb-0">New Properties</p>
                <p className="text-lg font-semibold mb-0">{mariaData?.recent_activity.new_properties}</p>
              </div>
              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground mb-0">New Markets</p>
                <p className="text-lg font-semibold mb-0">{mariaData?.recent_activity.new_markets}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Financial Metrics Card */}
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Financial Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground mb-0">Avg Monthly Profit</p>
                <p className="text-lg font-semibold mb-0">
                  ${mariaData?.property_analytics.financial_metrics.avg_monthly_profit.toLocaleString()}
                </p>
              </div>
              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground mb-0">Avg Rent</p>
                <p className="text-lg font-semibold mb-0">${mariaData?.property_analytics.financial_metrics.avg_rent.toLocaleString()}</p>
              </div>
              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground mb-0">Total Investment Opportunity</p>
                <p className="text-lg font-semibold mb-0">
                  ${mariaData?.property_analytics.financial_metrics.total_investment_opportunity.toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Status Distribution Card */}
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <PieChart className="w-5 h-5" />
              Status Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {mariaData?.maria_status_distribution.map((status, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={`status-${index}`} className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${status.status === "pending" ? "bg-yellow-400" : "bg-gray-400"}`} />
                    <p className="text-sm text-muted-foreground capitalize mb-0">{status.status}</p>
                  </div>
                  <p className="text-lg font-semibold mb-0">{status.count}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
